/**
 * TeamSection (HOF-627 §12) — Settings → Team, the farm's people.
 *
 * Since the settings refactor this is the Team **pane** (`/settings/team`): it mounts only while
 * that pane is open, so a visit to any other pane makes no members read — the role the
 * "Anzeigen" toggle used to play. The list itself, with its reads, is `TeamMembersList`.
 *
 * Phone and email are each person's login, so the owner never edits them here (§12 rule 1).
 */
import { useTranslation } from 'react-i18next';
import { SettingsPane } from '@hofsync/design-system';
import TeamMembersList from './TeamMembersList';
import LegacyInvitesSection from './LegacyInvitesSection';
import type { SettingsPaneChrome } from './paneChrome';

export interface TeamSectionProps extends SettingsPaneChrome {
    /** The seat may read the farm's members (`members.read`) — the list and its reads. */
    members?: boolean;
    /** The owner still manages pre-HOF-627 invitations with the old form (gated by the page). */
    legacyInvites?: boolean;
}

export default function TeamSection({ members = true, legacyInvites = false, ...chrome }: TeamSectionProps) {
    const { t } = useTranslation();
    const legacy = legacyInvites && <LegacyInvitesSection />;
    if (!members) {
        // The legacy owner invite form alone, for a seat with ``members.invite`` but no
        // ``members.read`` — no members read is made.
        return (
            <SettingsPane title={t('settings.team.title', 'Team')} {...chrome}>
                {legacy}
            </SettingsPane>
        );
    }
    return <TeamMembersList {...chrome}>{legacy}</TeamMembersList>;
}
